import axios from "axios";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

// ---------------------------------------------------------------------------
// REQUEST interceptor — attach the JWT stored in localStorage.
// ---------------------------------------------------------------------------
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("shakthi_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---------------------------------------------------------------------------
// RESPONSE interceptor
//
// - 401  →  wipe token, toast "Session expired", redirect to /login.
// - 5xx / network / timeout on GETs  →  retry up to 3 times with exponential
//   backoff (500ms · 1s · 2s). The retry loop NEVER wipes the JWT.
// - 4xx (except 429) → surface immediately to the caller.
//
// Note: 403 is used by the backend for legitimate business rules
// ("case already assigned", "profile not active" …) so we DO NOT auto-logout
// on 403 — pages already handle those messages inline.
// ---------------------------------------------------------------------------
let _sessionExpiredShown = false;
const RETRYABLE_METHODS = ["get", "head", "options"];
const RETRY_MAX = 3;
const RETRY_BASE_MS = 500;

function _isRetryableError(err) {
  const method = (err?.config?.method || "").toLowerCase();
  if (!RETRYABLE_METHODS.includes(method)) return false;
  // Network error / timeout → no response
  if (!err.response) return true;
  const s = err.response.status;
  return s === 429 || (s >= 500 && s < 600);
}

function _handleSessionExpired() {
  if (_sessionExpiredShown) return;
  _sessionExpiredShown = true;
  try {
    localStorage.removeItem("shakthi_token");
    localStorage.removeItem("shakthi_user");
  } catch { /* ignore */ }
  try { toast.error("Session expired — please log in again"); } catch { /* ignore */ }
  // Give the toast one frame to paint, then redirect. Avoid loop if already there.
  setTimeout(() => {
    if (!/\/login\b/.test(window.location.pathname)) {
      window.location.href = "/login";
    }
    _sessionExpiredShown = false;
  }, 400);
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err?.config;
    const status = err?.response?.status;

    // 401 → hard logout (never retry: refreshing an expired token is impossible
    // without a refresh-token flow, and quiet retries would just spam 401s).
    if (status === 401) {
      _handleSessionExpired();
      return Promise.reject(err);
    }

    // Retry transient failures on safe methods
    if (cfg && _isRetryableError(err)) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= RETRY_MAX) {
        const delay = RETRY_BASE_MS * Math.pow(2, cfg.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        return api.request(cfg);
      }
    }

    return Promise.reject(err);
  },
);

export default api;

export const errorMessage = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
};
